import React, { useState } from 'react';
import { UserPlus, Shield, Ban, CheckCircle, Trash2, Edit2, X } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
export function Users() {
  const {
    users,
    roles,
    currentWorkspace,
    currentUser,
    updateUserStatus,
    updateUserRole,
    deleteUser
  } = useWorkspace();
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number>(0);
  const canManageUsers = currentUser.role === 'Super Admin' || currentUser.role === 'Workspace Admin';
  const handleStartEditRole = (userId: number, currentRoleId: number) => {
    setEditingUserId(userId);
    setSelectedRoleId(currentRoleId);
  };
  const handleSaveRole = (userId: number) => {
    if (selectedRoleId) {
      updateUserRole(userId, selectedRoleId);
    }
    setEditingUserId(null);
  };
  const getRoleBadgeColor = (roleName: string) => {
    switch (roleName) {
      case 'Super Admin':
        return 'bg-purple-100 text-purple-800';
      case 'Workspace Admin':
        return 'bg-blue-100 text-blue-800';
      case 'Subregion Admin':
        return 'bg-indigo-100 text-indigo-800';
      case 'Operator':
        return 'bg-green-100 text-green-800';
      case 'Viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-2112">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2113">
        <div className="min-w-0" data-id="element-2114">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2115">Team Users</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2116">
            Manage access to the {currentWorkspace.name} workspace.
          </p>
        </div>
        {canManageUsers && <button className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-2117">
            <UserPlus className="h-4 w-4 flex-shrink-0" data-id="element-2118" />
            <span data-id="element-2119">Invite User</span>
          </button>}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2120">
        {users.length === 0 ? <div className="p-12 text-center" data-id="element-2121">
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-2122">
              No users found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-2123">
              Invite team members to collaborate.
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-2124">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2125">
              <thead className="bg-gray-50" data-id="element-2126">
                <tr data-id="element-2127">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2128">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2129">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2130">
                    Status
                  </th>
                  {canManageUsers && <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2131">
                      Actions
                    </th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2132">
                {users.map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-2133">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2134">
                      <div className="flex items-center" data-id="element-2135">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 mr-3 flex-shrink-0" data-id="element-2136">
                          {user.name.charAt(0)}
                        </div>
                        <div className="min-w-0" data-id="element-2137">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-2138">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-[200px]" data-id="element-2139">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2140">
                      {editingUserId === user.id ? <div className="flex items-center gap-2" data-id="element-2141">
                          <select className="rounded-md border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500" value={selectedRoleId} onChange={e => setSelectedRoleId(parseInt(e.target.value))} data-id="element-2142">
                            {roles.map(role => <option key={role.id} value={role.id} data-id="element-2143">
                                {role.name}
                              </option>)}
                          </select>
                          <button onClick={() => handleSaveRole(user.id)} className="p-1 text-green-600 hover:text-green-800 flex-shrink-0" data-id="element-2144">
                            <CheckCircle className="h-4 w-4" data-id="element-2145" />
                          </button>
                          <button onClick={() => setEditingUserId(null)} className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0" data-id="element-2146">
                            <X className="h-4 w-4" data-id="element-2147" />
                          </button>
                        </div> : <div className="flex items-center gap-2" data-id="element-2148">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getRoleBadgeColor(user.role)}`} data-id="element-2149">
                            <Shield className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-2150" />
                            {user.role}
                          </span>
                          {canManageUsers && user.id !== currentUser.id && <button onClick={() => handleStartEditRole(user.id, user.roleId)} className="p-1 text-gray-400 hover:text-blue-600 flex-shrink-0" title="Change Role" data-id="element-2151">
                              <Edit2 className="h-3 w-3" data-id="element-2152" />
                            </button>}
                        </div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2153">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${user.status === 'active' ? 'bg-green-100 text-green-800' : user.status === 'suspended' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-2154">
                        {user.status}
                      </span>
                    </td>
                    {canManageUsers && <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2155">
                        {user.id !== currentUser.id && <div className="flex items-center justify-end gap-2" data-id="element-2156">
                            {user.status === 'active' ? <button onClick={() => updateUserStatus(user.id, 'suspended')} className="text-yellow-600 hover:text-yellow-900 flex-shrink-0" title="Suspend User" data-id="element-2157">
                                <Ban className="h-4 w-4" data-id="element-2158" />
                              </button> : <button onClick={() => updateUserStatus(user.id, 'active')} className="text-green-600 hover:text-green-900 flex-shrink-0" title="Activate User" data-id="element-2159">
                                <CheckCircle className="h-4 w-4" data-id="element-2160" />
                              </button>}
                            <button onClick={() => deleteUser(user.id)} className="text-red-600 hover:text-red-900 flex-shrink-0" title="Delete User" data-id="element-2161">
                              <Trash2 className="h-4 w-4" data-id="element-2162" />
                            </button>
                          </div>}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}